import { useState } from "react"

import {
  api,
  type Ficha as FichaDoContrato,
  type ItemDeConferenciaNaTela,
  type LinhaDeRegularidade,
  type ValidacaoNaTela,
} from "../api"
import { Button } from "../componentes/Button"
import { CampoDeData } from "../componentes/CampoDeData"
import { ConfirmarComSenha, ResumoDoAto } from "../componentes/ConfirmarComSenha"
import * as Icones from "../componentes/Icones"
import { Input } from "../componentes/Input"
import { Textarea } from "../componentes/Textarea"
import { NativeSelect } from "../componentes/NativeSelect"
import { RotuloDoCampo } from "../componentes/RotuloDoCampo"
import { SeloDoValor } from "../componentes/Selo"
import { Column } from "../componentes/TablePrincipal2"
import { toast } from "../componentes/Toast"
import { data, plural, rotulo } from "../formato"
import { BOTAO_CANCELAR, GRADE_FORMULARIO, MAIUSCULA } from "../lib/classes"
import { cn } from "../lib/cn"
import { GRAVIDADES_DA_OCORRENCIA, GRAVIDADE_INICIAL } from "../lib/gravidades"
import { BlocoEditavel, mensagemDaFalha } from "./Campos"
import { GavetaDaFicha } from "./Gaveta"
import { EstadoDoPedido, usePedido } from "./Pedido"
import { BotoesDaLinha, COLUNA_DE_ACOES, DetalheDaFicha, TabelaDaFicha, VazioDaFicha, useLinhasAbertas } from "./Tabela"

type Ocorrencia = FichaDoContrato["ocorrencias"][number]

const NUMERO = "tabular-nums"

const NOME_DA_REGULARIDADE: Record<string, string> = {
  valida: "Válida",
  vencida: "Vencida",
  sem_registro: "Sem registro",
}

const NOME_DA_VALIDACAO: Record<string, string> = {
  confirmada: "Confirmada no órgão",
  negada: "Há pendência no órgão",
  divergente: "Diverge do registro",
  nao_configurado: "Sem convênio",
  indisponivel: "Órgão não respondeu",
}

export function BlocoConferencia({ contratoId, aoMudar }: { contratoId: number; aoMudar?: () => void }) {
  const pedido = usePedido(() => api.conferencia(contratoId), `conferencia-${contratoId}`)
  const linhas = useLinhasAbertas<number>()

  const marcar = (item: ItemDeConferenciaNaTela, situacao: "conferido" | "pendente") =>
    api.marcarConferencia(item.id, situacao, null).then(() => {
      pedido.recarregar()
      aoMudar?.()
    })

  const conferir = (item: ItemDeConferenciaNaTela) => {
    marcar(item, "conferido")
      .then(() =>
        toast.success("Item conferido", {
          description: item.rotulo,
          action: {
            label: "Desfazer",
            onClick: () => {
              marcar(item, "pendente")
                .then(() => toast.success("Item voltou a pendente", { description: item.rotulo }))
                .catch((falha: unknown) => toast.error(mensagemDaFalha(falha)))
            },
          },
        }),
      )
      .catch((falha: unknown) => toast.error(mensagemDaFalha(falha)))
  }

  return (
    <BlocoEditavel
      titulo="Conferência mensal"
      editavel={false}
      motivoSemEdicao="cada item se marca na própria linha"
      recado={
        "A rotina que o fiscal refaz todo mês, montada pela natureza do " +
        "contrato: serviço com mão de obra pede folha de ponto, INSS e FGTS; " +
        "fornecimento não pede. Cada item traz o dispositivo de onde veio."
      }
      leitura={
        <EstadoDoPedido pedido={pedido} oQue="a conferência do mês">
          {(mes) => (
            <div className="flex flex-col gap-2">
              <p className="m-0 text-xs text-muted-foreground">
                Competência de {data(mes.competencia)} · {plural(mes.pendentes, "item ainda sem resolução", "itens ainda sem resolução")}
              </p>
              <TabelaDaFicha<ItemDeConferenciaNaTela>
                dados={mes.itens}
                vazio={<VazioDaFicha titulo="Nenhum item de conferência para este contrato neste mês" />}
                linhaClicavel={(item) => item.fundamento !== null || item.observacao !== null}
                aoClicarNaLinha={(item) => linhas.alternar(item.id)}
                linhaAberta={(item) => linhas.aberta(item.id)}
                detalheDaLinha={(item) => (
                  <div className="flex flex-col gap-2">
                    {item.fundamento !== null && <DetalheDaFicha rotulo="Fundamento">{item.fundamento}</DetalheDaFicha>}
                    {item.observacao !== null && <DetalheDaFicha rotulo="Observação">{item.observacao}</DetalheDaFicha>}
                  </div>
                )}
              >
                <Column<ItemDeConferenciaNaTela> name="rotulo" label="Item" render={(_v, item) => <span className={MAIUSCULA}>{item.rotulo}</span>} />
                <Column<ItemDeConferenciaNaTela>
                  name="situacao"
                  label="Situação"
                  render={(_v, item) => (
                    <SeloDoValor valor={item.situacao === "pendente" ? "pendente_de_conferencia" : item.situacao} />
                  )}
                />
                <Column<ItemDeConferenciaNaTela>
                  name="acoes"
                  label="Ações"
                  classe={COLUNA_DE_ACOES}
                  render={(_v, item) => (
                    <BotoesDaLinha
                      acoes={[item.situacao !== "conferido" && { rotulo: "Conferir", icone: Icones.Conferida, aoClicar: () => conferir(item) }]}
                    />
                  )}
                />
              </TabelaDaFicha>
            </div>
          )}
        </EstadoDoPedido>
      }
    />
  )
}

export function BlocoOcorrencias({
  ficha,
  aoAtualizar,
  pedidoDeEdicao,
  aoAtenderPedido,
}: {
  ficha: FichaDoContrato
  aoAtualizar: (ficha: FichaDoContrato) => void
  pedidoDeEdicao?: number
  aoAtenderPedido?: () => void
}) {
  const [rascunho, definirRascunho] = useState({
    titulo: "",
    descricao: "",
    gravidade: GRAVIDADE_INICIAL,
    providencia: "",
  })
  const linhas = useLinhasAbertas<number>()

  return (
    <BlocoEditavel
      titulo="Ocorrências"
      inclui
      edicao="gaveta"
      tituloDaGaveta="Registrar ocorrência"
      mensagemDeSucesso="Ocorrência registrada"
      pedidoDeEdicao={pedidoDeEdicao}
      aoAtenderPedido={aoAtenderPedido}
      falta={rascunho.titulo.trim() === "" ? "Falta o que aconteceu" : null}
      pedeSenha={{
        titulo: "Registrar a ocorrência",
        rotuloDoAto: "Registrar",
        resumo: (
          <ResumoDoAto
            itens={[
              ["Ocorrência", rascunho.titulo.trim()],
              ["Gravidade", rotulo(rascunho.gravidade)],
              ...(rascunho.providencia.trim() === ""
                ? []
                : [["Providência", rascunho.providencia.trim()] as [string, string]]),
            ]}
          />
        ),
      }}
      recado={
        "O registro próprio da execução: o que aconteceu, quando, com que " +
        "gravidade e o que se fez a respeito. A gravidade ordena a leitura de " +
        "quem for decidir — ela não bloqueia pagamento nem aplica sanção."
      }
      leitura={
        <TabelaDaFicha<Ocorrencia>
          dados={ficha.ocorrencias}
          ordenar={{ data: (o) => o.data }}
          vazio={
            <VazioDaFicha
              titulo="Nenhuma ocorrência registrada"
              descricao="Clique em Adicionar para registrar a primeira. Registrar exige estar designado neste contrato."
            />
          }
          linhaClicavel={(o) => o.descricao.trim() !== ""}
          aoClicarNaLinha={(o) => linhas.alternar(o.id)}
          linhaAberta={(o) => linhas.aberta(o.id)}
          detalheDaLinha={(o) => <DetalheDaFicha rotulo="Descrição">{o.descricao}</DetalheDaFicha>}
        >
          <Column<Ocorrencia> name="titulo" label="Ocorrência" render={(_v, o) => <span className={cn("font-medium", MAIUSCULA)}>{o.titulo}</span>} />
          <Column<Ocorrencia> name="data" label="Data" render={(_v, o) => <span className={NUMERO}>{data(o.data)}</span>} />
          <Column<Ocorrencia> name="gravidade" label="Gravidade" render={(_v, o) => <span className={MAIUSCULA}>{rotulo(o.gravidade)}</span>} />
          <Column<Ocorrencia> name="providencia" label="Providência" render={(_v, o) => <span className={MAIUSCULA}>{o.providencia ?? "—"}</span>} />
        </TabelaDaFicha>
      }
      formulario={() => (
        <div className={GRADE_FORMULARIO}>
          <div className="col-span-full">
            <RotuloDoCampo htmlFor="ocorrencia-titulo" obrigatorio>
              O que aconteceu
            </RotuloDoCampo>
            <Input
              id="ocorrencia-titulo"
              required
              value={rascunho.titulo}
              onChange={(e) => definirRascunho({ ...rascunho, titulo: e.target.value })}
            />
          </div>
          <div className="col-span-full">
            <RotuloDoCampo htmlFor="ocorrencia-descricao">Descrição</RotuloDoCampo>
            <Textarea
              id="ocorrencia-descricao"
              value={rascunho.descricao}
              onChange={(e) => definirRascunho({ ...rascunho, descricao: e.target.value })}
            />
          </div>
          <div>
            <RotuloDoCampo htmlFor="ocorrencia-gravidade" obrigatorio>
              Gravidade
            </RotuloDoCampo>
            <NativeSelect
              id="ocorrencia-gravidade"
              required
              name="gravidade-da-ocorrencia"
              autoComplete="off"
              value={rascunho.gravidade}
              onChange={(e) => definirRascunho({ ...rascunho, gravidade: e.target.value })}
            >
              {GRAVIDADES_DA_OCORRENCIA.map((g) => (
                <option key={g.valor} value={g.valor}>
                  {g.rotulo}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="col-span-full">
            <RotuloDoCampo htmlFor="ocorrencia-providencia">Providência tomada</RotuloDoCampo>
            <Textarea
              id="ocorrencia-providencia"
              value={rascunho.providencia}
              onChange={(e) => definirRascunho({ ...rascunho, providencia: e.target.value })}
            />
          </div>
        </div>
      )}
      aoGravar={async (senha) => {
        await api.registrarOcorrencia(
          ficha.id,
          {
            titulo: rascunho.titulo.trim(),
            descricao: rascunho.descricao.trim(),
            gravidade: rascunho.gravidade,
            providencia: rascunho.providencia.trim() === "" ? null : rascunho.providencia.trim(),
          },
          senha,
        )
        aoAtualizar(await api.ficha(ficha.id))
        definirRascunho({
          titulo: "",
          descricao: "",
          gravidade: GRAVIDADE_INICIAL,
          providencia: "",
        })
      }}
    />
  )
}

function paraVencer(linha: LinhaDeRegularidade): string {
  if (linha.dias_para_vencer === null) return "—"
  if (linha.dias_para_vencer < 0) return `Vencida há ${plural(-linha.dias_para_vencer, "dia", "dias")}`
  return plural(linha.dias_para_vencer, "dia", "dias")
}

export function Regularidade({ contratoId, aoMudar }: { contratoId: number; aoMudar?: () => void }) {
  const [em, definirEm] = useState("")
  const pedido = usePedido(
    () => api.regularidade(contratoId, em === "" ? undefined : em),
    `regularidade-${contratoId}-${em}`,
  )
  const [registrando, definirRegistrando] = useState<LinhaDeRegularidade | null>(null)
  const linhas = useLinhasAbertas<string>()
  const [validacoes, definirValidacoes] = useState<ValidacaoNaTela[] | null>(null)
  const [validando, definirValidando] = useState(false)
  const respostas = useLinhasAbertas<string>()

  const validar = () => {
    definirValidando(true)
    api
      .validarRegularidade(contratoId)
      .then(definirValidacoes)
      .catch((falha: unknown) => toast.error(mensagemDaFalha(falha)))
      .finally(() => definirValidando(false))
  }

  return (
    <BlocoEditavel
      titulo="Regularidade do fornecedor"
      editavel={false}
      motivoSemEdicao="cada certidão se registra na própria linha"
      recado={
        "A validade é aferida na DATA DO ATESTE, e não na de hoje: certidão " +
        "vencida hoje continua hábil para liquidação e pagamento se estava " +
        "válida quando o ateste foi dado."
      }
      leitura={
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <RotuloDoCampo htmlFor="regularidade-em">Aferir a validade na data de</RotuloDoCampo>
              <CampoDeData id="regularidade-em" classeDaCaixa="w-auto min-w-[160px]" valor={em} aoMudar={definirEm} />
            </div>
            <Button type="button" size="sm" onClick={validar} disabled={validando}>
              <Icones.Conferida />
              {validando ? "Consultando os órgãos..." : "Consultar nos órgãos"}
            </Button>
          </div>
          <EstadoDoPedido pedido={pedido} oQue="a regularidade">
            {(dados) => (
              <div className="flex flex-col gap-2">
                <p className="m-0 text-xs text-muted-foreground">
                  A validade é aferida em <strong className="text-foreground">{data(dados.em)}</strong>. Informe a data do
                  ateste para conferir um pagamento passado.
                </p>
                <TabelaDaFicha<LinhaDeRegularidade>
                  dados={dados.linhas}
                  vazio={<VazioDaFicha titulo="Nenhuma certidão a acompanhar" />}
                  linhaClicavel={(linha) => linha.observacao !== null}
                  aoClicarNaLinha={(linha) => linhas.alternar(linha.tipo)}
                  linhaAberta={(linha) => linhas.aberta(linha.tipo)}
                  detalheDaLinha={(linha) => <DetalheDaFicha rotulo="Observação">{linha.observacao}</DetalheDaFicha>}
                >
                  <Column<LinhaDeRegularidade>
                    name="nome"
                    label="Certidão"
                    render={(_v, linha) => (
                      <span className="flex flex-col">
                        <span className={MAIUSCULA}>{linha.nome}</span>
                        {linha.numero !== null && <span className="text-xs text-muted-foreground">Nº {linha.numero}</span>}
                      </span>
                    )}
                  />
                  <Column<LinhaDeRegularidade>
                    name="situacao"
                    label="Situação"
                    render={(_v, linha) => <SeloDoValor valor={linha.situacao} rotulo={NOME_DA_REGULARIDADE[linha.situacao]} />}
                  />
                  <Column<LinhaDeRegularidade>
                    name="validade"
                    label="Válida até"
                    render={(_v, linha) => <span className={NUMERO}>{linha.validade === null ? "—" : data(linha.validade)}</span>}
                  />
                  <Column<LinhaDeRegularidade> name="dias_para_vencer" label="Para vencer" render={(_v, linha) => paraVencer(linha)} />
                  <Column<LinhaDeRegularidade>
                    name="acoes"
                    label="Ações"
                    classe={COLUNA_DE_ACOES}
                    render={(_v, linha) => (
                      <BotoesDaLinha
                        acoes={[
                          {
                            rotulo: linha.situacao === "sem_registro" ? "Registrar a certidão" : "Atualizar a certidão",
                            icone: Icones.Anotacao,
                            aoClicar: () => definirRegistrando(linha),
                          },
                          linha.onde_obter !== null && {
                            rotulo: "Emitir no órgão",
                            icone: Icones.Abrir,
                            aoClicar: () => window.open(linha.onde_obter ?? "", "_blank", "noreferrer"),
                          },
                        ]}
                      />
                    )}
                  />
                </TabelaDaFicha>
              </div>
            )}
          </EstadoDoPedido>

          <GavetaDaFicha
            aberta={registrando !== null}
            titulo={registrando === null ? "Certidão" : `Certidão: ${registrando.nome}`}
            aoFechar={() => definirRegistrando(null)}
          >
            {registrando !== null && (
              <FormularioDaCertidao
                key={registrando.tipo}
                contratoId={contratoId}
                linha={registrando}
                aoCancelar={() => definirRegistrando(null)}
                aoRegistrar={() => {
                  toast.success("Certidão registrada", { description: registrando.nome })
                  definirRegistrando(null)
                  pedido.recarregar()
                  aoMudar?.()
                }}
              />
            )}
          </GavetaDaFicha>

          {validacoes !== null && (
            <div className="flex flex-col gap-2">
              <h3 className="m-0 text-sm font-semibold text-foreground">O que o órgão respondeu</h3>
              <TabelaDaFicha<ValidacaoNaTela>
                dados={validacoes}
                vazio={<VazioDaFicha titulo="Nenhuma resposta dos órgãos" />}
                aoClicarNaLinha={(v) => respostas.alternar(v.tipo)}
                linhaAberta={(v) => respostas.aberta(v.tipo)}
                detalheDaLinha={(v) => (
                  <div className="flex flex-col gap-2">
                    <DetalheDaFicha rotulo="Resposta">{v.mensagem}</DetalheDaFicha>
                    <DetalheDaFicha rotulo="O que fazer">{v.o_que_fazer}</DetalheDaFicha>
                    {v.divergencias.length > 0 && <DetalheDaFicha rotulo="Divergências">{v.divergencias.join(" · ")}</DetalheDaFicha>}
                    {v.credencial_que_falta !== null && (
                      <DetalheDaFicha rotulo="Credencial a obter">{v.credencial_que_falta}</DetalheDaFicha>
                    )}
                  </div>
                )}
              >
                <Column<ValidacaoNaTela> name="nome" label="Certidão" render={(_v, v) => <span className={MAIUSCULA}>{v.nome}</span>} />
                <Column<ValidacaoNaTela>
                  name="situacao"
                  label="Situação"
                  render={(_v, v) => <SeloDoValor valor={v.situacao} rotulo={NOME_DA_VALIDACAO[v.situacao]} />}
                />
                <Column<ValidacaoNaTela> name="fonte" label="Fonte" render={(_v, v) => v.fonte} />
              </TabelaDaFicha>
            </div>
          )}
        </div>
      }
    />
  )
}

export function FormularioDaCertidao({
  contratoId,
  linha,
  aoCancelar,
  aoRegistrar,
}: {
  contratoId: number
  linha: LinhaDeRegularidade
  aoCancelar: () => void
  aoRegistrar: () => void
}) {
  const [numero, definirNumero] = useState(linha.numero ?? "")
  const [emissao, definirEmissao] = useState(linha.emissao ?? "")
  const [validade, definirValidade] = useState(linha.validade ?? "")
  const [observacao, definirObservacao] = useState(linha.observacao ?? "")
  const [confirmando, definirConfirmando] = useState(false)

  const gravar = (senha: string) =>
    api
      .registrarCertidao(
        contratoId,
        {
          tipo: linha.tipo,
          validade,
          numero: numero.trim() === "" ? null : numero.trim(),
          emissao: emissao === "" ? null : emissao,
          observacao: observacao.trim() === "" ? null : observacao.trim(),
        },
        senha,
      )
      .then(aoRegistrar)

  return (
    <div data-slot="formulario-da-certidao" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <RotuloDoCampo htmlFor={`val-${linha.tipo}`} obrigatorio>
            Válida até
          </RotuloDoCampo>
          <CampoDeData id={`val-${linha.tipo}`} obrigatorio classeDaCaixa="w-auto min-w-[160px]" valor={validade} aoMudar={definirValidade} />
        </div>
        <div>
          <RotuloDoCampo htmlFor={`emi-${linha.tipo}`}>Emitida em</RotuloDoCampo>
          <CampoDeData id={`emi-${linha.tipo}`} classeDaCaixa="w-auto min-w-[160px]" valor={emissao} aoMudar={definirEmissao} />
        </div>
        <div className="flex-[1_1_240px]">
          <RotuloDoCampo htmlFor={`num-${linha.tipo}`}>Número ou código de controle</RotuloDoCampo>
          <Input id={`num-${linha.tipo}`} value={numero} onChange={(evento) => definirNumero(evento.target.value)} />
        </div>
      </div>
      <div>
        <RotuloDoCampo htmlFor={`obs-cert-${linha.tipo}`}>Observação</RotuloDoCampo>
        <Input id={`obs-cert-${linha.tipo}`} value={observacao} onChange={(evento) => definirObservacao(evento.target.value)} />
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" size="sm" className={BOTAO_CANCELAR} onClick={aoCancelar}>
          <Icones.Fechar />
          Cancelar
        </Button>
        <Button
          title={validade === "" ? "Falta a data de validade" : undefined}
          type="button"
          size="sm"
          disabled={confirmando || validade === ""}
          onClick={() => definirConfirmando(true)}
        >
          <Icones.Salvar />
          Salvar a certidão
        </Button>
      </div>
      <ConfirmarComSenha
        aberto={confirmando}
        titulo="Registrar a certidão"
        resumo={
          <ResumoDoAto
            itens={[
              ["Certidão", linha.nome],
              ["Válida até", validade === "" ? "—" : data(validade)],
              ...(numero.trim() === "" ? [] : [["Número", numero.trim()] as [string, string]]),
            ]}
          />
        }
        rotuloDoAto="Salvar a certidão"
        iconeDoAto={<Icones.Salvar />}
        aoConfirmar={gravar}
        aoFechar={() => definirConfirmando(false)}
      />
    </div>
  )
}

